import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, Outlet } from "react-router-dom";
import api from "@/lib/api";
import AgentNav from "@/components/AgentNav";
import AgentStickyBottomBar from "@/components/AgentStickyBottomBar";
import { AgentProvider } from "@/lib/agentContext";

/** Adjust a #RRGGBB hex by percent (-100..100). +ve lightens, -ve darkens. */
function shadeHex(hex, percent) {
  if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return hex;
  const num = parseInt(hex.slice(1), 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  const t = percent < 0 ? 0 : 255;
  const p = Math.abs(percent) / 100;
  r = Math.round((t - r) * p + r);
  g = Math.round((t - g) * p + g);
  b = Math.round((t - b) * p + b);
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
}

/** Fetches the agent by `:slug`, provides AgentContext to nested routes via <Outlet/>. */
export default function AgentLayout() {
  const { slug } = useParams();
  const [agent, setAgent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/agent/${slug}`)
      .then(({ data }) => { if (!cancelled) setAgent(data.agent); })
      .catch(() => { if (!cancelled) setError("Agent not found."); });
    return () => { cancelled = true; };
  }, [slug]);

  const brandStyle = useMemo(() => {
    if (!agent?.accent_color || !/^#[0-9A-Fa-f]{6}$/.test(agent.accent_color)) return undefined;
    return {
      "--sg-gold": agent.accent_color,
      "--sg-gold-soft": shadeHex(agent.accent_color, 15),
      "--sg-gold-deep": shadeHex(agent.accent_color, -20),
    };
  }, [agent?.accent_color]);

  if (error) {
    return (
      <div className="min-h-screen bg-sg-paper flex items-center justify-center">
        <div className="text-center">
          <div className="overline text-sg-gold-deep mb-3">404</div>
          <h1 className="font-display text-4xl text-sg-navy">{error}</h1>
          <Link to="/" className="pill-outline inline-flex items-center gap-2 mt-6">Back to home</Link>
        </div>
      </div>
    );
  }
  if (!agent) {
    return (
      <div className="min-h-screen bg-sg-paper flex items-center justify-center text-sg-mute animate-pulse">
        Loading branded portal…
      </div>
    );
  }

  return (
    <AgentProvider agent={agent}>
      <div className="min-h-screen bg-sg-paper flex flex-col" style={brandStyle} data-testid="agent-microsite-root">
        <AgentNav agent={agent} />
        <main className="flex-1 pb-24 lg:pb-0"><Outlet /></main>
        <footer className="border-t border-sg-line py-10 bg-sg-paper">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-6 text-sm text-sg-mute">
            <div>
              <div className="font-display text-lg text-sg-navy italic">{agent.name}</div>
              {agent.agency_name && (
                <div className="text-xs text-sg-gold-deep mt-1" data-testid="footer-agency-name">{agent.agency_name}</div>
              )}
              <p className="mt-2 text-xs">{agent.micro_market} · {agent.primary_locality} specialist</p>
              <p className="text-xs mt-1 font-mono">MahaCRITI Reg No: {agent.maharera_number}</p>
            </div>
            <div>
              <div className="overline text-sg-gold-deep mb-2">EXPLORE</div>
              <ul className="space-y-1.5">
                <li><Link to={`/agent/${slug}/buy`} className="hover:text-sg-navy">Buy</Link></li>
                <li><Link to={`/agent/${slug}/rent`} className="hover:text-sg-navy">Rent</Link></li>
                <li><Link to={`/agent/${slug}/new-launches`} className="hover:text-sg-navy">New Launches</Link></li>
                <li><Link to={`/agent/${slug}/market-insights`} className="hover:text-sg-navy">Market Insights</Link></li>
              </ul>
            </div>
            <div>
              <div className="overline text-sg-gold-deep mb-2">SELL & LEASE</div>
              <ul className="space-y-1.5">
                <li><Link to={`/agent/${slug}/sell`} className="hover:text-sg-navy">Sell My Property</Link></li>
                <li><Link to={`/agent/${slug}/sell`} className="hover:text-sg-navy">Lease Out</Link></li>
                <li><Link to={`/agent/${slug}/financials`} className="hover:text-sg-navy">Financials</Link></li>
              </ul>
            </div>
            <div>
              <div className="overline text-sg-gold-deep mb-2">CONTACT</div>
              <ul className="space-y-1.5">
                {agent.phone && <li>{agent.phone}</li>}
                <li><Link to={`/agent/${slug}/about`} className="hover:text-sg-navy">About {agent.name.split(" ")[0]}</Link></li>
                <li><Link to="/terms" className="hover:text-sg-navy" data-testid="agent-footer-terms-link">Terms &amp; Conditions</Link></li>
                <li><Link to="/" className="hover:text-sg-navy">Powered by INPropPortal</Link></li>
              </ul>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-8 text-xs text-sg-mute border-t border-sg-line pt-6 flex flex-wrap justify-between gap-2">
            <span>© 2026 · {agent.name} · MahaCRITI-aligned</span>
            <span className="font-mono">Powered by INPropPortal.com</span>
          </div>
        </footer>
        <AgentStickyBottomBar agent={agent} />
      </div>
    </AgentProvider>
  );
}
